// src/utils/pendingMessagesDb.ts
import type { Message } from '@/mocks/app/mockChatData';
import { paraClonavel } from './clonavel';

const DB_NAME = 'WhizChat_OfflineDB';
const STORE_NAME = 'pendingUploads';

// Inicializa o Banco de Dados no navegador
export const initOfflineDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Salva a mensagem e o arquivo no disco do navegador.
 *
 * ⚠️ `paraClonavel` NÃO é zelo preventivo: o `put` clona com structured clone, que
 * recusa Proxy, e `{ ...message }` copia só o topo — os campos aninhados continuam
 * proxies do Vue. `Message.reactions` é um `Map`, e o Vue proxia `Map`, então
 * gravar uma mensagem pendente estourava
 * `DOMException: Proxy object could not be cloned`. Como o chamador usa
 * `void savePendingMessageToDB(...)`, isso aparecia como `Uncaught (in promise)`
 * sem nada apontando para cá — foi o segundo erro do envio de vídeo grande.
 *
 * O `try` existe pelo mesmo motivo: este cache é conveniência (sobreviver a um F5).
 * Falhar a gravação não pode virar exceção não tratada no console de quem só
 * tentou mandar uma mensagem.
 */
export const savePendingMessageToDB = async (chatId: string, message: Message) => {
    try {
        const db = await initOfflineDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(paraClonavel({ ...message, __chatId: chatId }));
        return await new Promise((resolve) => tx.oncomplete = resolve);
    } catch (err) {
        console.warn('[pendingMessages] não foi possível guardar a mensagem pendente:', err);
        return undefined;
    }
};

// Remove da fila quando o envio dá certo
export const deletePendingMessageFromDB = async (messageId: string) => {
    const db = await initOfflineDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(messageId);
    return new Promise((resolve) => tx.oncomplete = resolve);
};

// Puxa as mensagens que ficaram "presas" no F5
export const getPendingMessagesFromDB = async (chatId: string): Promise<Message[]> => {
    const db = await initOfflineDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    
    return new Promise((resolve, reject) => {
        request.onsuccess = () => {
            const all = request.result as (Message & { __chatId: string })[];
            
            // Filtra pelo chat aberto e já marca como ERRO, pois se sobrou no banco pós-F5, a requisição quebrou
            const filtered = all
                .filter(m => m.__chatId === chatId)
                .map(m => {
                    const cleanMessage = { ...m };
                    delete (cleanMessage as any).__chatId;
                    cleanMessage.readStatus = 'error';
                    // Bolha vermelha sempre; botão "Reenviar" só para o que o reenvio atende
                    // (mesma lista da guarda em `retryFailedMessage` e de `PodeReenviar`).
                    cleanMessage.uploadError = ['text', 'file', 'imagem', 'video'].includes(cleanMessage.type);
                    return cleanMessage;
                });
            resolve(filtered);
        };
        request.onerror = () => reject(request.error);
    });
};